/**
 * fp-ts's `set` works with custom equality and ordering, which makes many operations cost O(n)
 * that costs O(1) on a classic set, including basic `has` and `add` methods.
 * This module provides functional code for working with a set that assumes === for equality
 * and no ordering (which may break some purity principles like consistent output from conversion
 * to array, but O(n log n) complexity of ordering is often not worth that).
 */
import { array, option, set } from 'fp-ts';
import type { Option } from 'fp-ts/lib/Option';
import { type Predicate, flow, identity, pipe } from 'fp-ts/lib/function';

import { nonEmptyArray } from '../types/NonEmptyArray';
import { safeAbsurd } from '../types/safeAbsurd';
import { ___, partialApply } from './partialApply';
import { prop } from './prop';

const filter: {
  <A, B extends A>(f: (x: A) => x is B): (s: Set<A>) => Set<B>;
  <A>(f: (x: A) => boolean): (s: Set<A>) => Set<A>;
} =
  <A>(f: (x: A) => boolean) =>
  (s: Set<A>) => {
    const result = new Set<A>();
    for (const x of s) {
      if (f(x)) {
        result.add(x);
      }
    }
    return result;
  };

const filterMap =
  <A, B>(f: (x: A) => Option<B>) =>
  (s: Set<A>): Set<B> => {
    const result = new Set<B>();
    s.forEach(
      flow(
        f,
        option.map((x) => result.add(x)),
      ),
    );
    return result;
  };

const insertMutable =
  <A>(a: A) =>
  (s: Set<A>): Set<A> => {
    s.add(a);
    return s;
  };

const insertToMutable =
  <A>(s: Set<A>) =>
  (a: A): Set<A> => {
    s.add(a);
    return s;
  };

const chain =
  <A, B>(f: (x: A) => Set<B>) =>
  (s: Set<A>): Set<B> => {
    const result: Set<B>[] = [];
    for (const x of s) {
      result.push(f(x));
    }
    return unionAll(...result);
  };

const map =
  <A, B>(f: (x: A) => B) =>
  (s: Set<A>): Set<B> => {
    const result = new Set<B>();
    for (const x of s) {
      result.add(f(x));
    }
    return result;
  };

const forEach =
  <A>(f: (x: A) => void) =>
  (s: Set<A>): void => {
    for (const x of s) {
      f(x);
    }
  };

const reduce =
  <A, B>(b: B, f: (acc: B, a: A) => B) =>
  (s: Set<A>): B => {
    let result = b;
    for (const el of s) {
      result = f(result, el);
    }
    return result;
  };

const diff =
  <A>(...subtract: Set<A>[]) =>
  (from: Set<A>): Set<A> =>
    pipe(
      from,
      filter((element) => !subtract.some((otherSet) => otherSet.has(element))),
    );

const intersectionAll = <A>(...sets: Set<A>[]): Set<A> => {
  if (!nonEmptyArray.isNonEmpty(sets)) {
    return set.empty;
  }

  const [firstSet, ...otherSets] = sets;
  return pipe(
    firstSet,
    filter((element) => otherSets.every((otherSet) => otherSet.has(element))),
  );
};

const union = <A>(...sets: Set<A>[]): ((set2: Set<A>) => Set<A>) =>
  partialApply(unionAll, ___, ...sets);

const unionAll = <A>(...sets: Set<A>[]): Set<A> =>
  new Set<A>(
    pipe(
      sets,
      array.chain<Set<A>, A>((x) => Array.from(x)),
    ),
  );

const compact: <A>(s: Set<Option<A>>) => Set<A> = flow(
  filter(option.isSome),
  map(prop('value')),
);

const fromArray = <A>(a: A[]): Set<A> => new Set(a);

const toArray = <A>(s: Set<A>): A[] => Array.from(s);

const insert =
  <A>(a: A) =>
  (s: Set<A>): Set<A> =>
    s.has(a) ? s : new Set(s).add(a);

const remove =
  <A>(a: A) =>
  (s: Set<A>): Set<A> => {
    if (s.has(a)) {
      const newSet = new Set(s);
      newSet.delete(a);
      return newSet;
    }
    return s;
  };

const has =
  <A>(predicate: Predicate<A>) =>
  (s: Set<A>): boolean => {
    const iterator = s.values();
    let result = iterator.next();
    while (!result.done) {
      if (predicate(result.value)) {
        return true;
      }
      result = iterator.next();
    }
    return false;
  };

const isInSet =
  <A>(s: Set<A>) =>
  (a: A): boolean =>
    s.has(a);

/**
 * Toggle element presence - if s contains a, remove it, otherwise add it.
 *
 * `presentInResult` can be used for features covering insert/remove/toggle driven by optional boolean.
 */
const toggle: <A>(a: A, presentInResult?: boolean) => (s: Set<A>) => Set<A> = (
  a,
  presentInResult,
) => {
  switch (presentInResult) {
    case true:
      return insert(a);
    case false:
      return remove(a);
    case undefined:
      return (s) => (s.has(a) ? remove(a) : insert(a))(s);
    default:
      safeAbsurd(presentInResult);
      return identity;
  }
};

export const plainSet = {
  insert,
  remove,
  toggle,

  has,
  isInSet,

  compact,
  filter,
  filterMap,
  insertMutable,
  insertToMutable,
  reduce,
  chain,
  map,
  forEach,

  diff,
  intersectionAll,
  union,
  unionAll,

  fromArray,
  toArray,
};
